import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Plus, Edit, Trash2, CalendarClock, X, UserPlus, ImageIcon, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import { translateStatus } from '../i18n/translations';

const BUREAU_DAYS = { urgent: 4, prioritaire: 8, classique: 10, 'non urgent': 15 };
const CLIENT_DAYS_FR = { urgent: 19, prioritaire: 21, classique: 30, 'non urgent': 45 };
const CLIENT_DAYS_VN = { urgent: 8, prioritaire: 10, classique: 15, 'non urgent': 30 };

function calcPreviewDates(orderDate, urgency, company) {
  if (!orderDate || !urgency) return null;
  const d = new Date(orderDate);
  if (isNaN(d.getTime())) return null;
  const bDays = BUREAU_DAYS[urgency] || 10;
  const cDays = company === 'Panther France' ? (CLIENT_DAYS_FR[urgency] || 30) : (CLIENT_DAYS_VN[urgency] || 15);
  const bureau = new Date(d); bureau.setDate(bureau.getDate() + bDays);
  const client = new Date(d); client.setDate(client.getDate() + cDays);
  return { bureau, client };
}

const EMPTY_LINE = { product_id: '', product_name: '', quantity: 1, unit_cost: 0, weight_kg: 0, unit_price: 0 };

export const Orders = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [orders, setOrders] = useState([]);
  const [commercials, setCommercials] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCompany, setSelectedCompany] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);

  const [formError, setFormError] = useState('');
  const [invalidFields, setInvalidFields] = useState({});

  // Client autocomplete
  const [clientSearch, setClientSearch] = useState('');
  const [clientResults, setClientResults] = useState([]);
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [clientDialogOpen, setClientDialogOpen] = useState(false);
  const [newClientForm, setNewClientForm] = useState({ company_name: '', name: '', firstname: '', address: '', postal_code: '', city: '', country: '', phone: '', email: '' });

  // Order lines
  const [orderLines, setOrderLines] = useState([{ ...EMPTY_LINE }]);

  const [formData, setFormData] = useState({
    order_number: '',
    client_name: '',
    client_firstname: '',
    client_address: '',
    client_country: '',
    client_phone: '',
    client_email: '',
    num_pieces: 1,
    piece_type: '',
    order_date: new Date().toISOString().slice(0, 16),
    urgency: 'classique',
    status: 'commandée',
    tracking_link: '',
    tracking_number: '',
    commercial_id: '',
    material_cost: 0,
    transport_cost: 0,
    sale_price_eur: 0,
    company: 'Panther VN',
    dropbox_link: ''
  });

  const [eurVndRate, setEurVndRate] = useState(25000);
  const [transportEstimate, setTransportEstimate] = useState(null);

  // Image upload state
  const [pendingImages, setPendingImages] = useState([]);
  const [imagePopupOpen, setImagePopupOpen] = useState(false);
  const [imagePopupImages, setImagePopupImages] = useState([]);

  // Advanced filters
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCommercial, setFilterCommercial] = useState('all');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');

  const filteredOrders = React.useMemo(() => {
    return orders.filter(o => {
      if (filterStatus !== 'all' && o.status !== filterStatus) return false;
      if (filterCommercial !== 'all' && o.commercial_id !== filterCommercial) return false;
      if (filterDateFrom) {
        const from = new Date(filterDateFrom);
        const oDate = new Date(o.order_date);
        if (oDate < from) return false;
      }
      if (filterDateTo) {
        const to = new Date(filterDateTo);
        to.setHours(23, 59, 59);
        const oDate = new Date(o.order_date);
        if (oDate > to) return false;
      }
      return true;
    });
  }, [orders, filterStatus, filterCommercial, filterDateFrom, filterDateTo]);

  useEffect(() => {
    fetchOrders();
    if (user?.role === 'admin') {
      fetchCommercials();
      fetchProducts();
      fetchSettings();
    }
  }, [selectedCompany]);

  const fetchOrders = async () => {
    try {
      const companyParam = selectedCompany !== 'all' ? `?company=${encodeURIComponent(selectedCompany)}` : '';
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/orders${companyParam}`, { withCredentials: true });
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCommercials = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/commercials`, { withCredentials: true });
      setCommercials(response.data);
    } catch (error) {
      console.error('Error fetching commercials:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/products`, { withCredentials: true });
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/settings`, { withCredentials: true });
      setEurVndRate(res.data.eur_vnd_rate);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const fetchTransportEstimate = async (country, weight) => {
    if (!country) { setTransportEstimate(null); return; }
    try {
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/transport-costs/estimate?country=${encodeURIComponent(country)}&weight=${weight}`, { withCredentials: true });
      setTransportEstimate(res.data);
    } catch (error) {
      setTransportEstimate(null);
    }
  };

  // totalWeight calculation moved here for useEffect dependency
  const totalWeight = orderLines.reduce((sum, l) => sum + (l.quantity * l.weight_kg), 0);

  // Auto-fetch transport estimate when country or weight changes
  useEffect(() => {
    if (dialogOpen && formData.client_country) {
      fetchTransportEstimate(formData.client_country, totalWeight);
    }
  }, [formData.client_country, totalWeight, dialogOpen]);

  // Client search with debounce
  const searchClients = useCallback(async (query) => {
    if (query.length < 1) { setClientResults([]); setShowClientDropdown(false); return; }
    try {
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/clients?search=${encodeURIComponent(query)}`, { withCredentials: true });
      setClientResults(res.data);
      setShowClientDropdown(true);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => { if (clientSearch) searchClients(clientSearch); }, 300);
    return () => clearTimeout(timer);
  }, [clientSearch, searchClients]);

  const selectClient = (client) => {
    setFormData({
      ...formData,
      client_name: client.name,
      client_firstname: client.firstname || '',
      client_address: client.address || '',
      client_country: client.country || '',
      client_phone: client.phone || '',
      client_email: client.email || ''
    });
    setClientSearch(`${client.company_name ? client.company_name + ' - ' : ''}${client.name} ${client.firstname || ''}`);
    setShowClientDropdown(false);
  };

  const handleCreateClient = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/clients`, newClientForm, { withCredentials: true });
      toast.success('Client créé');
      setClientDialogOpen(false);
      selectClient(res.data);
      setNewClientForm({ company_name: '', name: '', firstname: '', address: '', postal_code: '', city: '', country: '', phone: '', email: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  // Order lines management
  const addLine = () => setOrderLines([...orderLines, { ...EMPTY_LINE }]);

  const removeLine = (index) => {
    if (orderLines.length <= 1) return;
    setOrderLines(orderLines.filter((_, i) => i !== index));
  };

  const updateLine = (index, field, value) => {
    const newLines = [...orderLines];
    newLines[index] = { ...newLines[index], [field]: value };
    // If product changed, fill unit_cost, weight_kg, unit_price from product data
    if (field === 'product_id') {
      const product = products.find(p => p.id === value);
      if (product) {
        newLines[index].product_name = product.name;
        newLines[index].unit_cost = product.unit_cost;
        newLines[index].weight_kg = product.weight_kg;
        newLines[index].unit_price = product.selling_price || 0;
      }
    }
    setOrderLines(newLines);
  };

  const totalMaterialCost = orderLines.reduce((sum, l) => sum + (l.quantity * l.unit_cost), 0);
  const totalSalePrice = orderLines.reduce((sum, l) => sum + (l.quantity * l.unit_price), 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setInvalidFields({});

    // Explicit field validation
    const errors = {};
    if (!formData.order_number.trim()) errors.order_number = true;
    if (!formData.client_name.trim()) errors.client_name = true;
    if (!formData.client_country.trim()) errors.client_country = true;
    if (!formData.client_phone.trim()) errors.client_phone = true;
    if (!formData.client_email.trim()) errors.client_email = true;
    if (!formData.order_date) errors.order_date = true;
    if (!formData.commercial_id) errors.commercial_id = true;

    if (Object.keys(errors).length > 0) {
      setInvalidFields(errors);
      const fieldNames = [];
      if (errors.order_number) fieldNames.push('N° Commande');
      if (errors.client_name) fieldNames.push('Client');
      if (errors.client_country) fieldNames.push('Pays');
      if (errors.client_phone) fieldNames.push('Téléphone');
      if (errors.client_email) fieldNames.push('Email');
      if (errors.order_date) fieldNames.push('Date de commande');
      if (errors.commercial_id) fieldNames.push('Commercial');
      const errMsg = `Champs obligatoires manquants : ${fieldNames.join(', ')}`;
      setFormError(errMsg);
      toast.error(errMsg);
      return;
    }

    try {
      const hasLines = orderLines.some(l => l.product_id);
      const payload = {
        ...formData,
        order_date: new Date(formData.order_date).toISOString(),
        num_pieces: hasLines ? orderLines.reduce((s, l) => s + parseInt(l.quantity || 0), 0) : parseInt(formData.num_pieces),
        material_cost: hasLines ? totalMaterialCost : parseFloat(formData.material_cost),
        transport_cost: hasLines && transportEstimate?.found ? transportEstimate.estimated_cost : parseFloat(formData.transport_cost),
        sale_price_eur: hasLines ? totalSalePrice : parseFloat(formData.sale_price_eur),
        dropbox_link: formData.dropbox_link || '',
        order_lines: hasLines ? orderLines.filter(l => l.product_id).map(l => ({
          product_id: l.product_id,
          product_name: l.product_name,
          quantity: parseInt(l.quantity) || 1,
          unit_cost: parseFloat(l.unit_cost) || 0,
          weight_kg: parseFloat(l.weight_kg) || 0,
          unit_price: parseFloat(l.unit_price) || 0
        })) : []
      };

      if (editingOrder) {
        await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/orders/${editingOrder.id}`, payload, { withCredentials: true });
        // Upload pending images
        for (const file of pendingImages) {
          const fd = new FormData();
          fd.append('file', file);
          await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/orders/${editingOrder.id}/images`, fd, { withCredentials: true });
        }
        toast.success('Commande modifiée avec succès');
      } else {
        const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/orders`, payload, { withCredentials: true });
        // Upload pending images to newly created order
        for (const file of pendingImages) {
          const fd = new FormData();
          fd.append('file', file);
          await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/orders/${res.data.id}/images`, fd, { withCredentials: true });
        }
        toast.success('Commande créée avec succès');
      }
      setDialogOpen(false);
      setEditingOrder(null);
      resetForm();
      fetchOrders();
    } catch (error) {
      const errMsg = error.response?.data?.detail || "Erreur lors de l'enregistrement";
      setFormError(errMsg);
      toast.error(errMsg);
    }
  };

  const handleDelete = async (orderId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette commande ?')) return;
    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/orders/${orderId}`, { withCredentials: true });
      toast.success('Commande supprimée');
      fetchOrders();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const resetForm = () => {
    setFormData({
      order_number: '', client_name: '', client_firstname: '', client_address: '',
      client_country: '', client_phone: '', client_email: '',
      num_pieces: 1, piece_type: '',
      order_date: new Date().toISOString().slice(0, 16),
      urgency: 'classique', status: 'commandée',
      tracking_link: '', tracking_number: '', commercial_id: '',
      material_cost: 0, transport_cost: 0, sale_price_eur: 0, company: 'Panther VN',
      dropbox_link: ''
    });
    setOrderLines([{ ...EMPTY_LINE }]);
    setClientSearch('');
    setShowClientDropdown(false);
    setInvalidFields({});
    setFormError('');
    setPendingImages([]);
  };

  const handleEdit = (order) => {
    setEditingOrder(order);
    setFormData({
      order_number: order.order_number,
      client_name: order.client_name,
      client_firstname: order.client_firstname || '',
      client_address: order.client_address || '',
      client_country: order.client_country,
      client_phone: order.client_phone,
      client_email: order.client_email,
      num_pieces: order.num_pieces,
      piece_type: order.piece_type,
      order_date: new Date(order.order_date).toISOString().slice(0, 16),
      urgency: order.urgency,
      status: order.status,
      tracking_link: order.tracking_link,
      tracking_number: order.tracking_number,
      commercial_id: order.commercial_id,
      material_cost: order.material_cost,
      transport_cost: order.transport_cost,
      sale_price_eur: order.sale_price_eur,
      company: order.company,
      dropbox_link: order.dropbox_link || ''
    });
    setClientSearch(`${order.client_name} ${order.client_firstname || ''}`);
    if (order.order_lines && order.order_lines.length > 0) {
      setOrderLines(order.order_lines);
    } else {
      setOrderLines([{ ...EMPTY_LINE }]);
    }
    setDialogOpen(true);
  };

  const getStatusBadge = (status) => {
    const styles = {
      'commandée': 'bg-blue-100 text-blue-800 border-blue-200',
      'impression': 'bg-purple-100 text-purple-800 border-purple-200',
      'couture': 'bg-indigo-100 text-indigo-800 border-indigo-200',
      'livraison bureau': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'en cours de livraison client': 'bg-orange-100 text-orange-800 border-orange-200',
      'livrée': 'bg-emerald-100 text-emerald-800 border-emerald-200'
    };
    return styles[status] || 'bg-slate-100 text-slate-800 border-slate-200';
  };

  const getUrgencyBadge = (urgency) => {
    const styles = {
      'urgent': 'bg-red-100 text-red-800 border-red-200',
      'prioritaire': 'bg-orange-100 text-orange-800 border-orange-200',
      'classique': 'bg-slate-100 text-slate-800 border-slate-200',
      'non urgent': 'bg-green-100 text-green-800 border-green-200'
    };
    return styles[urgency] || 'bg-slate-100 text-slate-800 border-slate-200';
  };

  const hasProductLines = orderLines.some(l => l.product_id);

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-semibold text-slate-900 tracking-tight">Commandes</h1>
            <p className="text-sm text-slate-600 mt-1">Gestion de toutes vos commandes</p>
          </div>
          <div className="flex gap-3">
            <Select value={selectedCompany} onValueChange={setSelectedCompany}>
              <SelectTrigger data-testid="company-filter" className="w-48 border-slate-200">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes les sociétés</SelectItem>
                <SelectItem value="Panther VN">Panther VN</SelectItem>
                <SelectItem value="Panther France">Panther France</SelectItem>
                <SelectItem value="Autre">Autre</SelectItem>
              </SelectContent>
            </Select>

            {user?.role === 'admin' && (
            <Dialog open={dialogOpen} onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open) { setEditingOrder(null); resetForm(); }
            }}>
              <DialogTrigger asChild>
                <Button data-testid="create-order-button" className="bg-slate-900 hover:bg-slate-800">
                  <Plus size={18} className="mr-2" />
                  Nouvelle commande
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingOrder ? 'Modifier la commande' : 'Nouvelle commande'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>N° Commande*</Label>
                      <Input data-testid="order-number-input" className={invalidFields.order_number ? 'border-red-500 ring-1 ring-red-500' : ''} value={formData.order_number} onChange={(e) => { setFormData({ ...formData, order_number: e.target.value }); setInvalidFields(prev => ({ ...prev, order_number: false })); }} required />
                    </div>
                    <div className="space-y-2">
                      <Label>Société*</Label>
                      <Select value={formData.company} onValueChange={(value) => setFormData({ ...formData, company: value })}>
                        <SelectTrigger data-testid="order-company-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Panther VN">Panther VN</SelectItem>
                          <SelectItem value="Panther France">Panther France</SelectItem>
                          <SelectItem value="Autre">Autre</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Client autocomplete */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Client*</Label>
                      <Button type="button" size="sm" variant="ghost" data-testid="add-client-button" onClick={() => setClientDialogOpen(true)} className="h-7 text-xs text-blue-600 hover:text-blue-700">
                        <UserPlus size={14} className="mr-1" />
                        Nouveau client
                      </Button>
                    </div>
                    <div className="relative">
                      <Input
                        data-testid="client-search-autocomplete"
                        className={invalidFields.client_name ? 'border-red-500 ring-1 ring-red-500' : ''}
                        value={clientSearch}
                        onChange={(e) => { setClientSearch(e.target.value); setInvalidFields(prev => ({ ...prev, client_name: false })); }}
                        placeholder="Rechercher par nom ou entreprise..."
                        onFocus={() => clientSearch && setShowClientDropdown(true)}
                        onBlur={() => setTimeout(() => setShowClientDropdown(false), 200)}
                      />
                      {showClientDropdown && clientResults.length > 0 && (
                        <div className="absolute z-50 w-full bg-white border border-slate-200 rounded-md shadow-lg mt-1 max-h-48 overflow-y-auto">
                          {clientResults.map((c) => (
                            <div
                              key={c.id}
                              data-testid="client-autocomplete-option"
                              className="px-3 py-2 hover:bg-slate-50 cursor-pointer text-sm"
                              onMouseDown={() => selectClient(c)}
                            >
                              <span className="font-medium">{c.name} {c.firstname}</span>
                              {c.company_name && <span className="text-slate-500 ml-2">({c.company_name})</span>}
                              {c.city && <span className="text-slate-400 ml-2">- {c.city}</span>}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Pays*</Label>
                      <Input className={invalidFields.client_country ? 'border-red-500 ring-1 ring-red-500' : ''} value={formData.client_country} onChange={(e) => { setFormData({ ...formData, client_country: e.target.value }); setInvalidFields(prev => ({ ...prev, client_country: false })); }} required />
                    </div>
                    <div className="space-y-2">
                      <Label>Téléphone*</Label>
                      <Input className={invalidFields.client_phone ? 'border-red-500 ring-1 ring-red-500' : ''} value={formData.client_phone} onChange={(e) => { setFormData({ ...formData, client_phone: e.target.value }); setInvalidFields(prev => ({ ...prev, client_phone: false })); }} required />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Email*</Label>
                    <Input type="email" className={invalidFields.client_email ? 'border-red-500 ring-1 ring-red-500' : ''} value={formData.client_email} onChange={(e) => { setFormData({ ...formData, client_email: e.target.value }); setInvalidFields(prev => ({ ...prev, client_email: false })); }} required />
                  </div>

                  {/* Product lines */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-semibold">Lignes de produits</Label>
                      <Button type="button" size="sm" variant="outline" data-testid="add-product-line-button" onClick={addLine} className="h-7 text-xs">
                        <Plus size={14} className="mr-1" />
                        Ajouter une ligne
                      </Button>
                    </div>
                    <div className="space-y-2">
                      {orderLines.map((line, idx) => (
                        <div key={idx} className="flex items-end gap-2 p-3 bg-slate-50 rounded-md border border-slate-200">
                          <div className="flex-1 space-y-1">
                            <Label className="text-xs text-slate-500">Produit</Label>
                            <Select value={line.product_id} onValueChange={(v) => updateLine(idx, 'product_id', v)}>
                              <SelectTrigger data-testid={`product-select-${idx}`} className="h-9"><SelectValue placeholder="Choisir..." /></SelectTrigger>
                              <SelectContent>
                                {products.map(p => (
                                  <SelectItem key={p.id} value={p.id}>{p.name} ({p.unit_cost.toFixed(2)}&euro; / {p.weight_kg}kg)</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="w-20 space-y-1">
                            <Label className="text-xs text-slate-500">Qté</Label>
                            <Input data-testid={`qty-input-${idx}`} type="number" min="1" className="h-9" value={line.quantity} onChange={(e) => updateLine(idx, 'quantity', parseInt(e.target.value) || 1)} />
                          </div>
                          <div className="w-24 space-y-1">
                            <Label className="text-xs text-slate-500">Prix unit.</Label>
                            <Input data-testid={`unit-price-input-${idx}`} type="number" step="0.01" min="0" className="h-9" value={line.unit_price} onChange={(e) => updateLine(idx, 'unit_price', parseFloat(e.target.value) || 0)} />
                          </div>
                          <div className="w-20 text-right space-y-1">
                            <Label className="text-xs text-slate-500">Sous-total</Label>
                            <p className="text-sm font-medium h-9 flex items-center justify-end">{(line.quantity * line.unit_price).toFixed(2)}&euro;</p>
                          </div>
                          {orderLines.length > 1 && (
                            <Button type="button" size="sm" variant="ghost" onClick={() => removeLine(idx)} className="h-9 w-9 p-0 text-red-500 hover:bg-red-50">
                              <X size={16} />
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>

                    {hasProductLines && (
                      <div data-testid="order-totals-preview" className="rounded-lg border border-slate-300 bg-white p-3 space-y-1">
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-600">Coût matière (auto)</span>
                          <span className="font-medium">{totalMaterialCost.toFixed(2)} &euro; / {Math.round(totalMaterialCost * eurVndRate).toLocaleString('vi-VN')} &dong;</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-600">Poids total</span>
                          <span className="font-medium">{totalWeight.toFixed(2)} kg</span>
                        </div>
                        <div className="flex justify-between text-sm border-t border-slate-200 pt-1 mt-1">
                          <span className="font-semibold text-slate-900">PRIX DE VENTE TOTAL</span>
                          <div className="text-right">
                            <span className="font-bold text-lg text-slate-900">{totalSalePrice.toFixed(2)} &euro;</span>
                            <span className="text-sm text-slate-500 ml-2">({Math.round(totalSalePrice * eurVndRate).toLocaleString('vi-VN')} &dong;)</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Transport estimation */}
                    {hasProductLines && transportEstimate && (
                      <div data-testid="transport-estimate" className="rounded-lg border border-blue-200 bg-blue-50 p-3 space-y-1">
                        <p className="text-xs font-medium text-blue-700 uppercase tracking-wider">ESTIMATION TRANSPORT ({formData.client_country})</p>
                        {transportEstimate.found ? (
                          <>
                            <div className="flex justify-between text-sm">
                              <span className="text-blue-600">Prix au kg</span>
                              <span className="font-medium">{transportEstimate.cost_per_kg.toFixed(2)} &euro;/kg</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-blue-600">Frais fixes</span>
                              <span className="font-medium">{transportEstimate.fixed_fee.toFixed(2)} &euro;</span>
                            </div>
                            <div className="flex justify-between text-sm border-t border-blue-200 pt-1">
                              <span className="font-semibold text-blue-900">COÛT TRANSPORT ESTIMÉ</span>
                              <span className="font-bold text-blue-900">{transportEstimate.estimated_cost.toFixed(2)} &euro;</span>
                            </div>
                          </>
                        ) : (
                          <p className="text-sm text-blue-500">Aucun tarif trouvé pour ce pays</p>
                        )}
                      </div>
                    )}

                    {/* Profit calculation */}
                    {hasProductLines && transportEstimate?.found && (
                      <div data-testid="profit-preview" className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 space-y-1">
                        <p className="text-xs font-medium text-emerald-700 uppercase tracking-wider">PROFIT ESTIMÉ</p>
                        <div className="flex justify-between text-sm">
                          <span className="text-emerald-600">Prix de vente</span>
                          <span className="font-medium">{totalSalePrice.toFixed(2)} &euro;</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-emerald-600">- Coût produits</span>
                          <span className="font-medium text-red-600">-{totalMaterialCost.toFixed(2)} &euro;</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-emerald-600">- Coût transport</span>
                          <span className="font-medium text-red-600">-{transportEstimate.estimated_cost.toFixed(2)} &euro;</span>
                        </div>
                        <div className="flex justify-between text-sm border-t border-emerald-200 pt-1">
                          <span className="font-bold text-emerald-900">PROFIT</span>
                          {(() => {
                            const profit = totalSalePrice - totalMaterialCost - transportEstimate.estimated_cost;
                            const marginPct = totalSalePrice !== 0 ? (profit / totalSalePrice) * 100 : 0;
                            return (
                              <div className="text-right">
                                <span className={`font-bold text-lg ${profit >= 0 ? 'text-emerald-900' : 'text-red-600'}`}>{profit.toFixed(2)} &euro;</span>
                                <span className="text-sm text-slate-500 ml-2">({Math.round(profit * eurVndRate).toLocaleString('vi-VN')} &dong;)</span>
                              </div>
                            );
                          })()}
                        </div>
                        {(() => {
                          const profit = totalSalePrice - totalMaterialCost - transportEstimate.estimated_cost;
                          const marginPct = totalSalePrice !== 0 ? (profit / totalSalePrice) * 100 : 0;
                          return (
                            <div data-testid="order-margin-percent" className="flex justify-between items-center pt-1">
                              <span className="font-semibold text-emerald-800 text-xs uppercase">MARGE %</span>
                              <span className={`font-bold text-sm ${marginPct >= 0 ? 'text-emerald-900' : 'text-red-600'}`}>{marginPct.toFixed(1)} %</span>
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Date de commande*</Label>
                      <Input type="datetime-local" value={formData.order_date} onChange={(e) => setFormData({ ...formData, order_date: e.target.value })} required />
                    </div>
                    <div className="space-y-2">
                      <Label>Urgence*</Label>
                      <Select value={formData.urgency} onValueChange={(value) => setFormData({ ...formData, urgency: value })}>
                        <SelectTrigger data-testid="order-urgency-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="urgent">Urgent</SelectItem>
                          <SelectItem value="prioritaire">Prioritaire</SelectItem>
                          <SelectItem value="classique">Classique</SelectItem>
                          <SelectItem value="non urgent">Non urgent</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Delivery date preview */}
                  {(() => {
                    const preview = calcPreviewDates(formData.order_date, formData.urgency, formData.company);
                    if (!preview) return null;
                    return (
                      <div data-testid="delivery-date-preview" className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                        <div className="flex items-center gap-2 mb-2">
                          <CalendarClock size={16} className="text-slate-500" />
                          <span className="text-xs font-medium text-slate-600 uppercase tracking-wider">Dates de livraison estimées</span>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <p className="text-xs text-slate-500">Livraison bureau</p>
                            <p className="text-sm font-semibold text-slate-900">{preview.bureau.toLocaleDateString('fr-FR')}</p>
                            <p className="text-xs text-slate-400">+{BUREAU_DAYS[formData.urgency] || 10} jours</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Livraison client</p>
                            <p className="text-sm font-semibold text-slate-900">{preview.client.toLocaleDateString('fr-FR')}</p>
                            <p className="text-xs text-slate-400">+{formData.company === 'Panther France' ? (CLIENT_DAYS_FR[formData.urgency] || 30) : (CLIENT_DAYS_VN[formData.urgency] || 15)} jours</p>
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Statut*</Label>
                      <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                        <SelectTrigger data-testid="order-status-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="commandée">Commandée</SelectItem>
                          <SelectItem value="impression">Impression</SelectItem>
                          <SelectItem value="couture">Couture</SelectItem>
                          <SelectItem value="livraison bureau">Livraison bureau</SelectItem>
                          <SelectItem value="en cours de livraison client">En cours de livraison client</SelectItem>
                          <SelectItem value="livrée">Livrée</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Commercial*</Label>
                      <Select value={formData.commercial_id} onValueChange={(value) => { setFormData({ ...formData, commercial_id: value }); setInvalidFields(prev => ({ ...prev, commercial_id: false })); }}>
                        <SelectTrigger data-testid="order-commercial-select" className={invalidFields.commercial_id ? 'border-red-500 ring-1 ring-red-500' : ''}><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                        <SelectContent>
                          {commercials.map((c) => (
                            <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Lien de suivi</Label>
                      <Input value={formData.tracking_link} onChange={(e) => setFormData({ ...formData, tracking_link: e.target.value })} />
                    </div>
                    <div className="space-y-2">
                      <Label>Numéro de suivi</Label>
                      <Input value={formData.tracking_number} onChange={(e) => setFormData({ ...formData, tracking_number: e.target.value })} />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>{t('dropbox_link')}</Label>
                    <Input data-testid="dropbox-link-input" value={formData.dropbox_link} onChange={(e) => setFormData({ ...formData, dropbox_link: e.target.value })} placeholder="https://dropbox.com/..." />
                  </div>

                  {/* Image Upload */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2"><ImageIcon size={16} /> {t('images')}</Label>
                    <div className="flex flex-wrap gap-2">
                      {/* Show existing images if editing */}
                      {editingOrder && editingOrder.images?.map((img, i) => (
                        <div key={i} className="relative">
                          <img src={`${process.env.REACT_APP_BACKEND_URL}${img}`} alt="" className="w-16 h-16 object-cover rounded border border-slate-300" />
                        </div>
                      ))}
                      {/* Show pending images */}
                      {pendingImages.map((file, i) => (
                        <div key={i} className="relative">
                          <img src={URL.createObjectURL(file)} alt="" className="w-16 h-16 object-cover rounded border border-emerald-300" />
                          <button type="button" onClick={() => setPendingImages(prev => prev.filter((_, j) => j !== i))} className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600">
                            <X size={12} />
                          </button>
                        </div>
                      ))}
                      <label data-testid="add-image-button" className="w-16 h-16 border-2 border-dashed border-slate-300 rounded flex flex-col items-center justify-center cursor-pointer hover:border-slate-400 transition-colors">
                        <Upload size={16} className="text-slate-400" />
                        <span className="text-[10px] text-slate-400 mt-0.5">Image</span>
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          className="hidden"
                          onChange={(e) => {
                            if (e.target.files) {
                              setPendingImages(prev => [...prev, ...Array.from(e.target.files)]);
                            }
                            e.target.value = '';
                          }}
                        />
                      </label>
                    </div>
                  </div>

                  {formError && (
                    <div data-testid="form-error" className="rounded-md border border-red-300 bg-red-50 p-3">
                      <p className="text-sm font-medium text-red-700">{formError}</p>
                    </div>
                  )}

                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); setEditingOrder(null); resetForm(); }}>Annuler</Button>
                    <Button data-testid="submit-order-button" type="submit" className="bg-slate-900 hover:bg-slate-800 uppercase">{editingOrder ? 'Modifier' : 'Créer'}</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
            )}
          </div>
        </div>

        {/* New Client Dialog */}
        <Dialog open={clientDialogOpen} onOpenChange={setClientDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Nouveau client</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateClient} className="space-y-4">
              <div className="space-y-2">
                <Label>Nom entreprise</Label>
                <Input data-testid="quick-client-company" value={newClientForm.company_name} onChange={(e) => setNewClientForm({ ...newClientForm, company_name: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nom*</Label>
                  <Input data-testid="quick-client-name" value={newClientForm.name} onChange={(e) => setNewClientForm({ ...newClientForm, name: e.target.value })} required />
                </div>
                <div className="space-y-2">
                  <Label>Prénom</Label>
                  <Input value={newClientForm.firstname} onChange={(e) => setNewClientForm({ ...newClientForm, firstname: e.target.value })} />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Adresse</Label>
                <Input value={newClientForm.address} onChange={(e) => setNewClientForm({ ...newClientForm, address: e.target.value })} />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Code postal</Label>
                  <Input value={newClientForm.postal_code} onChange={(e) => setNewClientForm({ ...newClientForm, postal_code: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Ville</Label>
                  <Input value={newClientForm.city} onChange={(e) => setNewClientForm({ ...newClientForm, city: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Pays</Label>
                  <Input value={newClientForm.country} onChange={(e) => setNewClientForm({ ...newClientForm, country: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Téléphone</Label>
                  <Input value={newClientForm.phone} onChange={(e) => setNewClientForm({ ...newClientForm, phone: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input type="email" value={newClientForm.email} onChange={(e) => setNewClientForm({ ...newClientForm, email: e.target.value })} />
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setClientDialogOpen(false)}>Annuler</Button>
                <Button data-testid="quick-client-submit" type="submit" className="bg-slate-900 hover:bg-slate-800">Créer le client</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        <Card className="border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg font-medium">{t('order_list')}</CardTitle>
            {/* Advanced Filters */}
            {user?.role === 'admin' && (
              <div data-testid="orders-advanced-filters" className="flex flex-wrap items-end gap-3 mt-3 pt-3 border-t border-slate-100">
                <div className="space-y-1">
                  <label className="text-xs text-slate-500 font-medium">{t('filter_by_status')}</label>
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger data-testid="filter-status" className="w-40 h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('all_statuses')}</SelectItem>
                      <SelectItem value="commandée">{translateStatus('commandée', t)}</SelectItem>
                      <SelectItem value="impression">{translateStatus('impression', t)}</SelectItem>
                      <SelectItem value="couture">{translateStatus('couture', t)}</SelectItem>
                      <SelectItem value="livraison bureau">{translateStatus('livraison bureau', t)}</SelectItem>
                      <SelectItem value="en cours de livraison client">{translateStatus('en cours de livraison client', t)}</SelectItem>
                      <SelectItem value="livrée">{translateStatus('livrée', t)}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-slate-500 font-medium">{t('filter_by_commercial')}</label>
                  <Select value={filterCommercial} onValueChange={setFilterCommercial}>
                    <SelectTrigger data-testid="filter-commercial" className="w-40 h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('all_commercials')}</SelectItem>
                      {commercials.map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-slate-500 font-medium">{t('from_date')}</label>
                  <Input data-testid="filter-date-from" type="date" className="w-36 h-8 text-xs" value={filterDateFrom} onChange={(e) => setFilterDateFrom(e.target.value)} />
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-slate-500 font-medium">{t('to_date')}</label>
                  <Input data-testid="filter-date-to" type="date" className="w-36 h-8 text-xs" value={filterDateTo} onChange={(e) => setFilterDateTo(e.target.value)} />
                </div>
                <Button data-testid="reset-filters-button" size="sm" variant="outline" className="h-8 text-xs" onClick={() => { setFilterStatus('all'); setFilterCommercial('all'); setFilterDateFrom(''); setFilterDateTo(''); }}>
                  {t('reset_filters')}
                </Button>
                <span className="text-xs text-slate-500 ml-auto">{filteredOrders.length} / {orders.length} {t('orders_count')}</span>
              </div>
            )}
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 mx-auto"></div>
              </div>
            ) : filteredOrders.length === 0 ? (
              <p className="text-center py-8 text-sm text-slate-500">{t('no_orders')}</p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="font-medium w-14"></TableHead>
                      <TableHead className="font-medium">N° Commande</TableHead>
                      <TableHead className="font-medium">Client</TableHead>
                      <TableHead className="font-medium">Pays</TableHead>
                      <TableHead className="font-medium">Pièces</TableHead>
                      <TableHead className="font-medium">Date</TableHead>
                      {user?.role === 'admin' && (
                        <>
                          <TableHead className="font-medium">Liv. bureau</TableHead>
                        </>
                      )}
                      <TableHead className="font-medium">Liv. client</TableHead>
                      <TableHead className="font-medium">Urgence</TableHead>
                      <TableHead className="font-medium">Statut</TableHead>
                      <TableHead className="font-medium">Commercial</TableHead>
                      <TableHead className="font-medium text-right">Commission</TableHead>
                      {user?.role === 'admin' && (
                        <>
                          <TableHead className="font-medium text-right">Coût matière</TableHead>
                          <TableHead className="font-medium text-right">Coût transport</TableHead>
                          <TableHead className="font-medium text-right">Prix vente</TableHead>
                          <TableHead className="font-medium text-right">Marge</TableHead>
                          <TableHead className="font-medium text-right">Poids (kg)</TableHead>
                          <TableHead className="font-medium text-right">Coût livr.</TableHead>
                        </>
                      )}
                      {user?.role === 'admin' && (
                        <TableHead className="font-medium">Société</TableHead>
                      )}
                      {user?.role === 'admin' && (
                        <TableHead className="font-medium">Actions</TableHead>
                      )}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredOrders.map((order) => (
                      <TableRow key={order.id} data-testid="order-row" className="hover:bg-slate-50">
                        <TableCell>
                          {order.images?.length > 0 ? (
                            <img src={`${process.env.REACT_APP_BACKEND_URL}${order.images[0]}`} alt="" data-testid="order-thumbnail" className="w-10 h-10 object-cover rounded border cursor-pointer hover:opacity-80" onClick={() => { setImagePopupImages(order.images); setImagePopupOpen(true); }} />
                          ) : (
                            <div className="w-10 h-10 bg-slate-200 rounded flex items-center justify-center"><ImageIcon size={14} className="text-slate-400" /></div>
                          )}
                        </TableCell>
                        <TableCell className="font-medium">{order.order_number}</TableCell>
                        <TableCell>{order.client_name}</TableCell>
                        <TableCell>{order.client_country}</TableCell>
                        <TableCell>{order.num_pieces} {order.piece_type}</TableCell>
                        <TableCell className="text-sm">{new Date(order.order_date).toLocaleDateString('fr-FR')}</TableCell>
                        {user?.role === 'admin' && (
                          <>
                            <TableCell className="text-sm">{order.bureau_delivery_date ? new Date(order.bureau_delivery_date).toLocaleDateString('fr-FR') : '-'}</TableCell>
                          </>
                        )}
                        <TableCell className="text-sm">{order.client_delivery_date ? new Date(order.client_delivery_date).toLocaleDateString('fr-FR') : '-'}</TableCell>
                        <TableCell>
                          <Badge className={`${getUrgencyBadge(order.urgency)} border text-xs uppercase`}>{order.urgency}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={`${getStatusBadge(order.status)} border text-xs uppercase`}>{translateStatus(order.status, t)}</Badge>
                        </TableCell>
                        <TableCell className="text-sm">{order.commercial_name}</TableCell>
                        <TableCell className="text-right font-medium text-emerald-700">{order.commercial_commission.toFixed(2)} &euro;</TableCell>
                        {user?.role === 'admin' && (
                          <>
                            <TableCell className="text-right">{order.material_cost?.toFixed(2)} &euro;</TableCell>
                            <TableCell className="text-right">{order.transport_cost?.toFixed(2)} &euro;</TableCell>
                            <TableCell className="text-right font-medium">{order.sale_price_eur?.toFixed(2)} &euro;</TableCell>
                            <TableCell className="text-right font-medium">{order.gross_margin?.toFixed(2)} &euro;</TableCell>
                            <TableCell className="text-right">{order.package_weight_kg || 0} kg</TableCell>
                            <TableCell className="text-right font-medium">{order.delivery_cost ? `${order.delivery_cost.toFixed(2)} \u20ac` : '-'}</TableCell>
                          </>
                        )}
                        {user?.role === 'admin' && (
                          <TableCell className="text-sm">{order.company}</TableCell>
                        )}
                        {user?.role === 'admin' && (
                          <TableCell>
                            <div className="flex gap-2">
                              <Button data-testid="edit-order-button" size="sm" variant="ghost" onClick={() => handleEdit(order)} className="h-8 w-8 p-0"><Edit size={16} /></Button>
                              <Button data-testid="delete-order-button" size="sm" variant="ghost" onClick={() => handleDelete(order.id)} className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"><Trash2 size={16} /></Button>
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
        <Dialog open={imagePopupOpen} onOpenChange={setImagePopupOpen}>
          <DialogContent className="max-w-2xl" data-testid="image-popup-dialog">
            <DialogHeader>
              <DialogTitle>{t('image_popup_title')}</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4">
              {imagePopupImages.map((img, i) => (
                <img key={i} src={`${process.env.REACT_APP_BACKEND_URL}${img}`} alt="" className="w-full rounded-lg border border-slate-200" />
              ))}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};
